// SEM-6345 (2) — screen names in an assistant answer become RAIL LINKS.
//
// The assistant is grounded in help articles whose screen names are already
// held to the nav manifest (tooling/spec-tools/check-kb-screen-names.mjs), so
// an answer that says "**Taleplerim** ekranından" is naming a menu entry
// verbatim. The link is therefore the SECOND field of one derivation, not a
// hand-attached decoration: the same manifest row carries the label and the
// href, and no route is ever invented.
//
// Two narrowings, both deliberate:
//   * the pool is the caller's VISIBLE menu (use-visible-menu), so a link is
//     never offered into a screen this caller's authority hides — the click
//     would land on a 403 the prose did not warn about;
//   * matching is CASE-SENSITIVE and whole-word. A wrong link is worse than
//     no link (ticket), and Turkish case folding ("İ"/"i") is exactly where a
//     loose match would start inventing them.

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { ReactNode } from "react";

import { menuLinks } from "@/components/menu-config";
import { useVisibleMenu } from "@/components/use-visible-menu";
import type { MarkdownTextDecorator } from "@/features/support-help/markdown";

interface ScreenTarget {
  readonly label: string;
  readonly href: string;
}

const WORD_CHAR = /[\p{L}\p{N}]/u;

function escapeRegExp(literal: string): string {
  return literal.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * A decorator that replaces every whole-word occurrence of a screen name with
 * an anchor to that screen. Longest label first, so "İzin takvimim" wins over
 * "İzin" where both are destinations.
 */
export function buildScreenLinkDecorator(
  targets: readonly ScreenTarget[],
): MarkdownTextDecorator {
  if (targets.length === 0) return (text): ReactNode => text;

  const byLabel = new Map(targets.map((target) => [target.label, target.href]));
  const pattern = new RegExp(
    [...byLabel.keys()]
      .sort((a, b) => b.length - a.length)
      .map(escapeRegExp)
      .join("|"),
    "gu",
  );

  return (text, edges): ReactNode => {
    const out: ReactNode[] = [];
    let last = 0;
    let i = 0;
    for (const match of text.matchAll(pattern)) {
      const start = match.index ?? 0;
      const end = start + match[0].length;
      // Whole-word only: "Talepler" inside "Talepleriniz" names no screen —
      // and the run's own edges answer for its ends, so a name bolded without
      // its Turkish suffix ("**Talepler**iniz") is not a screen name either.
      const before = start === 0 ? edges.before : (text[start - 1] ?? "");
      const after = end === text.length ? edges.after : (text[end] ?? "");
      if (WORD_CHAR.test(before) || WORD_CHAR.test(after)) continue;
      if (start > last) out.push(text.slice(last, start));
      out.push(
        <Link
          key={i++}
          to={byLabel.get(match[0])!}
          className="font-medium text-[var(--accent)] underline"
        >
          {match[0]}
        </Link>,
      );
      last = end;
    }
    if (out.length === 0) return text;
    if (last < text.length) out.push(text.slice(last));
    return out;
  };
}

/** The decorator for the live caller's visible menu. */
export function useScreenLinkDecorator(): MarkdownTextDecorator {
  const { t } = useTranslation();
  const { sections } = useVisibleMenu();

  // The memo key is the pool ITSELF, serialised — `sections` is rebuilt on
  // every render (use-visible-menu holds no reference), so depending on it
  // would recompile the alternation regex on every keystroke and every stream
  // delta while reading as though it did not.
  const pool = sections
    .flatMap((section) => menuLinks(section.items).map((item) => `${t(item.labelKey)}\u0000${item.href}`))
    .join("\u0001");

  return useMemo(() => {
    const hrefsByLabel = new Map<string, string | null>();
    for (const row of pool === "" ? [] : pool.split("\u0001")) {
      const [label = "", href = ""] = row.split("\u0000");
      // Two destinations under one name: neither is the right guess, so the
      // name stays prose. (The manifest forbids the collision; this keeps a
      // future one from producing a link to the wrong screen.)
      hrefsByLabel.set(label, hrefsByLabel.has(label) ? null : href);
    }
    const targets = [...hrefsByLabel.entries()]
      .filter((entry): entry is [string, string] => entry[1] !== null)
      .map(([label, href]) => ({ label, href }));
    return buildScreenLinkDecorator(targets);
  }, [pool]);
}
